/**
 * Filtro genérico de agencias por ubigeo con fallback distrito → provincia → departamento.
 * Generaliza la lógica de `codUbigeoShalom.ts` para que funcione con cualquier courier
 * (Olva, Shalom, Nortexpress, custom del tenant), no solo Shalom.
 */

export function normalizeGeo(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();
}

const PROVINCE_ALIASES: Record<string, string> = {
  NASCA: 'NAZCA',
  'CONTRALMIRANTE VILLAR': 'CONTRALMIRANTE VILLA',
  'PROV. CONST. DEL CALLAO': 'CALLAO',
};

export function normalizeProv(s: string): string {
  const base = normalizeGeo(s);
  return PROVINCE_ALIASES[base] ?? base;
}

const DISTRICT_ALIASES: Record<string, string> = {
  ATE: 'ATE-VITARTE',
  LIMA: 'CERCADO LIMA',
};

export function normalizeDist(s: string): string {
  const base = normalizeGeo(s);
  return DISTRICT_ALIASES[base] ?? base;
}

export type MatchedLevel = 'district' | 'province' | 'department' | 'none';

export interface AgencyUbigeoAccessors<T> {
  getDept: (agency: T) => string | null | undefined;
  getProv: (agency: T) => string | null | undefined;
  getDist: (agency: T) => string | null | undefined;
  /** Devuelve true si la agencia tiene una dirección utilizable (no vacía). */
  hasAddress?: (agency: T) => boolean;
}

export interface FilterAgenciesResult<T> {
  matched: T[];
  matchedLevel: MatchedLevel;
}

/**
 * Filtra agencias aplicando fallback progresivo distrito → provincia → departamento.
 * Si no hay agencias en el distrito, sube a provincia. Si tampoco hay, sube a departamento.
 *
 * Razón del fallback: para couriers con red densa (Shalom tiene 800+ agencias) un cliente
 * en un distrito sin agencia probablemente puede recoger en una agencia cercana de la provincia.
 *
 * @param agencies Lista de agencias del courier (puede venir de DB o de catálogo hardcoded).
 * @param address Dirección destino (department, province, district).
 * @param accessors Funciones para extraer dept/prov/dist y validar dirección.
 * @returns Agencias matched + nivel donde se encontró match (district / province / department / none).
 */
export function filterAgenciesByUbigeo<T>(
  agencies: T[],
  address: { department: string; province: string; district: string },
  accessors: AgencyUbigeoAccessors<T>,
): FilterAgenciesResult<T> {
  const { department, province, district } = address;
  const { getDept, getProv, getDist, hasAddress } = accessors;

  const normDept = normalizeGeo(department);
  const normProv = normalizeProv(province);
  const normDist = normalizeDist(district);

  const withAddr = (list: T[]) => (hasAddress ? list.filter(hasAddress) : list);

  const byDept = agencies.filter((a) => normalizeGeo(getDept(a) ?? '') === normDept);
  const byProv = byDept.filter((a) => normalizeProv(getProv(a) ?? '') === normProv);
  const byDist = byProv.filter((a) => normalizeDist(getDist(a) ?? '') === normDist);

  const distMatched = withAddr(byDist);
  if (distMatched.length > 0) return { matched: distMatched, matchedLevel: 'district' };

  const provMatched = withAddr(byProv);
  if (provMatched.length > 0) return { matched: provMatched, matchedLevel: 'province' };

  const deptMatched = withAddr(byDept);
  if (deptMatched.length > 0) return { matched: deptMatched, matchedLevel: 'department' };

  return { matched: [], matchedLevel: 'none' };
}
